<script lang="ts">
	import { Icon, Spinner, Text } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';

	export let icon: string;
	export let label: string;
	export let disabled: boolean = false;
	export let active: boolean = false;
	export let loading: boolean = false;
	export let variant: 'default' | 'success' | 'danger' | 'warning' = 'default';
	export let iconOnly: boolean = false;

	const dispatch = createEventDispatcher();

	const handleClick = () => {
		if (!disabled && !loading) {
			dispatch('click');
		}
	};
</script>

<button
	type="button"
	class="video-action-button variant-{variant}"
	class:active
	class:disabled
	class:loading
	class:icon-only={iconOnly}
	disabled={disabled || loading}
	aria-label={label}
	aria-pressed={active}
	aria-busy={loading}
	on:click={handleClick}
	on:pointerdown
	on:pointerup
	on:pointerleave
>
	<div class="icon-wrapper">
		{#if loading}
			<Spinner size="xs" darkMode />
		{:else}
			<Icon {icon} size="s" />
		{/if}
	</div>
	{#if !iconOnly}
		<Text styleType="label-extra-small" class="action-label">
			{label}
		</Text>
	{/if}
</button>

<style>
	.video-action-button {
		flex: 1 0 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-s, 8px);
		padding: 6px 4px;
		background-color: var(--color-1);
		border: none;
		border-radius: 8px;
		cursor: pointer;
		min-width: 0;
		min-height: 48px;
		transition: background-color 0.2s ease;
	}

	/* Icon-only mode */
	.video-action-button.icon-only {
		gap: 0;
	}

	.video-action-button:hover:not(.disabled):not(.loading) {
		background-color: var(--color-2);
	}

	.video-action-button:active:not(.disabled):not(.loading) {
		background-color: var(--color-3);
	}

	/* Active state */
	.video-action-button.active {
		background-color: var(--color-primary);
	}

	.video-action-button.active :global(.action-label) {
		color: var(--color-white);
	}

	/* Disabled state */
	.video-action-button.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	/* Loading state */
	.video-action-button.loading {
		cursor: default;
	}

	.video-action-button.loading :global(.action-label) {
		color: var(--color-3);
	}

	/* Success variant */
	.video-action-button.variant-success {
		background-color: var(--color-success-10);
	}

	.video-action-button.variant-success .icon-wrapper {
		color: var(--color-success);
	}

	.video-action-button.variant-success .icon-wrapper :global(*) {
		color: var(--color-success) !important;
	}

	.video-action-button.variant-success :global(.action-label) {
		color: var(--color-success);
	}

	.video-action-button.variant-success:hover:not(.disabled):not(.loading) {
		background-color: var(--color-success-20);
	}

	/* Danger variant */
	.video-action-button.variant-danger .icon-wrapper {
		color: var(--color-danger);
	}

	.video-action-button.variant-danger .icon-wrapper :global(*) {
		color: var(--color-danger) !important;
	}

	.video-action-button.variant-danger :global(.action-label) {
		color: var(--color-danger);
	}

	/* Warning variant */
	.video-action-button.variant-warning {
		background-color: var(--color-warning-10);
	}

	.video-action-button.variant-warning .icon-wrapper {
		color: var(--color-warning);
	}

	.video-action-button.variant-warning .icon-wrapper :global(*) {
		color: var(--color-warning) !important;
	}

	.video-action-button.variant-warning :global(.action-label) {
		color: var(--color-warning);
	}

	.video-action-button.variant-warning:hover:not(.disabled):not(.loading) {
		background-color: var(--color-warning-20);
	}

	.icon-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		color: var(--color-white);
	}

	.video-action-button :global(.action-label) {
		color: var(--color-muted);
		text-transform: uppercase;
		letter-spacing: 0.48px;
		font-size: 8px;
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		width: 100%;
		text-align: center;
	}
</style>
